import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Can from '../components/Can';
import { useCan } from '../hooks/useCan';
import { useSnackbar } from 'notistack';
import storeCustomerAccessService, {
  StoreB2bAccessRequest,
} from '../services/storeCustomerAccessService';
import { ROUTES } from '../config/routes';

const statusColor = (status: string): 'default' | 'warning' | 'success' | 'error' => {
  switch (status) {
    case 'Pending':
      return 'warning';
    case 'Approved':
      return 'success';
    case 'Rejected':
      return 'error';
    default:
      return 'default';
  }
};

const StoreB2bAccessRequestsPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const puedeAprobar = useCan('ventas.clientes.solicitudes-ecommerce.nuevo');
  const puedeRechazar = useCan('ventas.clientes.solicitudes-ecommerce.anular');
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<StoreB2bAccessRequest[]>([]);
  const [statusFilter, setStatusFilter] = useState('Pending');
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [selected, setSelected] = useState<StoreB2bAccessRequest | null>(null);
  const [nroCliente, setNroCliente] = useState('');
  const [priceListId, setPriceListId] = useState('');
  const [note, setNote] = useState('');
  const [working, setWorking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await storeCustomerAccessService.listRequests(
        statusFilter === 'All' ? undefined : statusFilter
      );
      setRows(data);
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || 'No se pudieron cargar las solicitudes', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, enqueueSnackbar]);

  useEffect(() => {
    void load();
  }, [load]);

  const openApprove = (row: StoreB2bAccessRequest) => {
    setSelected(row);
    setNroCliente(String(row.matchedNroCliente || row.requestedNroCliente || ''));
    setPriceListId('');
    setNote('');
    setApproveOpen(true);
  };

  const openReject = (row: StoreB2bAccessRequest) => {
    setSelected(row);
    setNote('');
    setRejectOpen(true);
  };

  const handleApprove = async () => {
    if (!puedeAprobar || !selected) return;
    setWorking(true);
    try {
      await storeCustomerAccessService.approveRequest(selected.id, {
        nroCliente: nroCliente ? Number(nroCliente) : null,
        priceListId: priceListId || null,
        sendInvite: true,
        note: note || null,
      });
      enqueueSnackbar('Solicitud aprobada e invitación enviada', { variant: 'success' });
      setApproveOpen(false);
      await load();
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || 'Error al aprobar', { variant: 'error' });
    } finally {
      setWorking(false);
    }
  };

  const handleReject = async () => {
    if (!puedeRechazar || !selected) return;
    setWorking(true);
    try {
      await storeCustomerAccessService.rejectRequest(selected.id, note || undefined);
      enqueueSnackbar('Solicitud rechazada', { variant: 'success' });
      setRejectOpen(false);
      await load();
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || 'Error al rechazar', { variant: 'error' });
    } finally {
      setWorking(false);
    }
  };

  return (
    <Box p={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" mb={2} gap={2}>
        <Typography variant="h5" fontWeight={700}>
          Solicitudes acceso ecommerce B2B
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Estado</InputLabel>
            <Select
              label="Estado"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="Pending">Pendientes</MenuItem>
              <MenuItem value="Approved">Aprobadas</MenuItem>
              <MenuItem value="Rejected">Rechazadas</MenuItem>
              <MenuItem value="All">Todas</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={() => navigate(ROUTES.ventas.clientes.list)}>
            Volver a clientes
          </Button>
        </Stack>
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>CUIT</TableCell>
                <TableCell>Razón social</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Teléfono</TableCell>
                <TableCell>Match sugerido</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Comentario</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No hay solicitudes
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{new Date(row.createdAt).toLocaleString()}</TableCell>
                  <TableCell>{row.cuit}</TableCell>
                  <TableCell>{row.businessName}</TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>{row.phone || '—'}</TableCell>
                  <TableCell>
                    {row.matchedNroCliente
                      ? `#${row.matchedNroCliente} ${row.matchedCustomerName || ''}`
                      : row.requestedNroCliente
                        ? `Pedido #${row.requestedNroCliente}`
                        : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={row.status} color={statusColor(row.status)} />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>{row.comment || '—'}</TableCell>
                  <TableCell align="right">
                    {row.status === 'Pending' && (
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Can permiso="ventas.clientes.solicitudes-ecommerce.nuevo">
                          <Button size="small" variant="contained" onClick={() => openApprove(row)}>
                            Aprobar
                          </Button>
                        </Can>
                        <Can permiso="ventas.clientes.solicitudes-ecommerce.anular">
                          <Button size="small" color="error" onClick={() => openReject(row)}>
                            Rechazar
                          </Button>
                        </Can>
                      </Stack>
                    )}
                    {row.matchedNroCliente && (
                      <Button
                        size="small"
                        onClick={() => navigate(ROUTES.ventas.clientes.editar(row.matchedNroCliente!))}
                      >
                        Ver cliente
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Aprobar solicitud</DialogTitle>
        <DialogContent>
          <Typography variant="body2" mb={2}>
            Se habilitará el cliente en la tienda y se enviará el email de activación a {selected?.email || 'quien solicitó el alta'}.
          </Typography>
          <TextField
            label="N° Cliente"
            value={nroCliente}
            onChange={(e) => setNroCliente(e.target.value)}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Lista precios ecommerce (opcional)"
            value={priceListId}
            onChange={(e) => setPriceListId(e.target.value)}
            fullWidth
            margin="normal"
            helperText="Vacío = lista por defecto de la tienda; si no hay, la comercial del cliente"
          />
          <TextField
            label="Nota"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveOpen(false)}>Cancelar</Button>
          <Can permiso="ventas.clientes.solicitudes-ecommerce.nuevo">
          <Button variant="contained" onClick={handleApprove} disabled={working || !nroCliente}>
            {working ? 'Procesando…' : 'Aprobar e invitar'}
          </Button>
          </Can>
        </DialogActions>
      </Dialog>

      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Rechazar solicitud</DialogTitle>
        <DialogContent>
          <TextField
            label="Motivo / nota"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Cancelar</Button>
          <Can permiso="ventas.clientes.solicitudes-ecommerce.anular">
          <Button color="error" variant="contained" onClick={handleReject} disabled={working}>
            {working ? 'Procesando…' : 'Rechazar'}
          </Button>
          </Can>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StoreB2bAccessRequestsPage;
